import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { ApiConstants } from 'src/app/constants/api-constants';
import { HttpGuardService } from 'src/app/guards/http-guard.service';
import { TabDefinition } from 'src/app/model';
import { ServicecallService } from 'src/app/services';
export interface ZoneData {
  _id: string;
  name: string;
  type: 'Circle' | 'Polygon';
  center?: [number, number];
  coordinates?: [number, number][];
  radius?: number;
  level: number;
  nature: string;
  centroid?: [number, number];
}

@Injectable({
  providedIn: 'root',
})
export class ZonesServiceService {
  constructor(
    private httpGuard: HttpGuardService,
    private _service: ServicecallService
  ) {}

  /**
   * Gets zones data
   * @param reqData
   * @returns
   */
  getZonesData(reqData: any) {
    let payload = {
      siteId: reqData.siteId,
      classType: reqData.classType,
      sortBy: reqData.sortBy,
    };
    if (reqData?.hierarchyId) {
      payload['hierarchyId'] = reqData?.hierarchyId;
    }
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      `${ApiConstants.URL.zoneDetails}${reqData.page}`,
      payload
    );
  }

  /**
   * Gets zone order
   * @param payload
   * @returns
   */
  getZoneOrder(payload: any) {
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      ApiConstants.URL.getZoneOrder,
      payload
    );
  }

  /**
   * Deletes zone
   * @param zoneId
   * @returns
   */
  deleteZone(zoneId: any) {
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      ApiConstants.URL.deleteZone,
      { zoneId }
    );
  }

  /* Create New Zone */
  createNewZone(
    siteId: any,
    classType: any,
    fileData: any,
    zoneDetails?: any,
    ptwDetails?: any,
    zoneId?: any,
    employeeList?: any,
    action?: any
  ) {
    const formData = {
      siteId: siteId,
      class: classType,
      zoneDetails: zoneDetails,
      ptwDetails: ptwDetails,
      zoneId: zoneId,
      employeeList: employeeList,
      action: action,
    };
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      ApiConstants.URL.createNewZone,
      formData
    );
  }

  /* Update New Zone */
  updateZoneDetails(
    name: any,
    siteId: any,
    zoneDetails: any,
    zoneId: any,
    fileData?: any,
    relativePath?: any,
    type?: any
  ) {
    const formData = {
      siteId: siteId,
      name: name,
      zoneDetails: zoneDetails,
      zoneId: zoneId,
      relativePath: relativePath,
      type: type,
    };
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      ApiConstants.URL.updateZoneDetails,
      formData
    );
  }

  /**
   * Gets zone hierarchy
   * @param siteId
   * @param order
   * @returns
   */
  getZoneHierarchy(siteId: any, order: any) {
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      ApiConstants.URL.getZoneHierarchy,
      { siteId, order }
    );
  }

  /**
   * Sites hierarchy info
   * @param siteId
   * @returns
   */
  siteHierarchyInfo(siteId: any) {
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      ApiConstants.URL.siteHierarchyInfo,
      { siteId: siteId }
    );
  }

  /**
   * Zones icon info
   * @returns
   */
  zoneIconInfo() {
    return this.httpGuard.httpReq(
      'get',
      'API_ENDPOINT',
      ApiConstants.URL.zoneIconInfo
    );
  }

  /**
   * Allocates zones
   * @param zoneId
   * @param siteId
   * @param action
   * @returns
   */
  allocateZones(zoneId: any, siteId: any, action: any) {
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      ApiConstants.URL.allocateZones,
      { zoneId, siteId, action }
    );
  }

  /**
   * Determines whether dependencies has
   * @param zoneId
   * @returns
   */
  hasDependencies(zoneId: any) {
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      ApiConstants.URL.hasDependencies,
      { zoneId }
    );
  }

  /**
   * Entitys site allocation
   * @param payload
   * @returns
   */
  entitySiteAllocation(payload: any) {
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      ApiConstants.URL.entitySiteAllocation,
      payload
    );
  }

  /**
   * Users mapping info
   * @param payload
   * @returns
   */
  usersMappingInfo(payload: any) {
    return this.httpGuard.httpReq(
      'post',
      'API_ENDPOINT',
      ApiConstants.URL.usersMappingInfo,
      payload
    );
  }

  /**
   * Gets lane details
   * @param payload
   * @returns
   */
  getLaneDetails(payload: any) {
    const params = '/' + payload?.siteId + '/zones/' + payload?.zoneId;
    return this.httpGuard.httpReq(
      'get',
      'API_ENDPOINT',
      ApiConstants.URL.fetchLanes,
      params
    );
  }

  /**
   * Zones with destination
   * @param payload
   * @returns
   */
  zonesWithDestination(payload: any) {
    const params = '/' + payload?.siteId + '/zones';
    return this.httpGuard.httpReq(
      'get',
      'API_ENDPOINT',
      ApiConstants.URL.fetchLanes,
      params
    );
  }

  /**
   * Tags history details
   * @param payload
   * @returns
   */
  tagHistoryDetails(payload: any) {
    const encodedFrom = encodeURIComponent(payload?.from);
    const encodedTo = encodeURIComponent(payload?.to);
    const params = payload?.zoneId
      ? '/tagHistory?zoneId=' +
        payload?.zoneId +
        '&from=' +
        encodedFrom +
        '&to=' +
        encodedTo +
        '&page=' +
        payload?.page +
        '&count=' +
        payload?.limit
      : '/tagHistory?from=' +
        encodedFrom +
        '&to=' +
        encodedTo +
        '&page=' +
        payload?.page +
        '&count=' +
        payload?.limit;
    return this.httpGuard.httpReq(
      'get',
      'API_ENDPOINT',
      ApiConstants.URL.entitiesDetails,
      params
    );
  }

  /**
   * Tags histroy download
   * @param payload
   * @returns
   */
  tagHistroyDownload(payload: any) {
    const encodedFrom = encodeURIComponent(payload?.from);
    const encodedTo = encodeURIComponent(payload?.to);
    const param =
      '/tagHistory/download?from=' + encodedFrom + '&to=' + encodedTo;
    return this.httpGuard.httpReq(
      'get-blob',
      'API_ENDPOINT',
      ApiConstants.URL.entitiesDetails,
      param
    );
  }

  /**
   * Get tab definitions with permission check functions
   * @param tenantCategory - The tenant category to check against
   * @returns Array of tab definitions with properly bound permission check functions
   */
  getTabDefinitions(tenantCategory: string | null): TabDefinition[] {
    return [
      {
        id: 'hierarchy',
        translationKey: 'hierarchy',
        visible: true,
        permissionCheck: () => this._service.rbacManagement('sites_Hierarchy'),
      },
      {
        id: 'zone',
        translationKey: 'zones',
        visible: true,
        permissionCheck: () => this._service.rbacManagement('sites_Zones'),
      },
      {
        id: 'tagHistory',
        translationKey: 'Tag History',
        visible: false,
        permissionCheck: () => tenantCategory === 'monotainer',
      },
      {
        id: 'statistics',
        translationKey: 'Statistics',
        visible: false,
        permissionCheck: () => this._service.rbacManagement('sites_Statistics'),
      },
      {
        id: 'ptw',
        translationKey: 'Permit To Work',
        visible: false,
        permissionCheck: () =>
          this._service.rbacManagement('sites_PermitToWork'),
      },
      {
        id: 'device',
        translationKey: 'Devices',
        visible: false,
        permissionCheck: () => this._service.rbacManagement(44),
      },
      {
        id: 'settings',
        translationKey: 'Settings',
        visible: false,
        permissionCheck: () => this._service.rbacManagement('sies_Settings'),
      },
      {
        id: 'iocl',
        translationKey: 'IOCL',
        visible: false,
        permissionCheck: () => this._service.rbacManagement('sites_IoclRouting'),
      },
    ];
  }
  /**
   * Get site hierarchy zones optimized for add/edit operations
   * Shows only parent zone (based on restrictions.hierarchy) and current zone
   */
  getSiteHierarchyZones(
    siteId: string,
    hierarchyParentId?: string,
    currentZoneId?: string
  ): Observable<ZoneData[]> {
    return this._service.siteHierarchyInfo(siteId).pipe(
      map((response: any) => {
        if (!response?.status || !response?.response?.data?.length) {
          return [];
        }

        const mapLevels = response.response.data;
        return this.extractFilteredZoneData(
          mapLevels,
          hierarchyParentId,
          currentZoneId
        );
      }),
      tap((zones: ZoneData[]) => {
        // console.log(`Extracted ${zones.length} zones for hierarchy display`);
      })
    );
  }
  /**
   * Extract zones based on restrictions.hierarchy parent-child relationship
   */
  private extractFilteredZoneData(
    mapLevels: any[],
    hierarchyParentId?: string,
    currentZoneId?: string
  ): ZoneData[] {
    // If no hierarchyParentId, it's the first entry - return empty array
    if (!hierarchyParentId) {
      return [];
    }

    const allElements = this.flattenMapElements(mapLevels);
    const zones: ZoneData[] = [];

    // Find and add the parent zone (based on restrictions.hierarchy)
    const parentZone = allElements.find((el) => el._id === hierarchyParentId);
    if (parentZone) {
      const parentZoneData = this.transformElementToZoneData(parentZone);
      if (parentZoneData) {
        zones.push(parentZoneData);
      }
    }

    // Find and add the current zone (if in edit mode)
    if (currentZoneId) {
      const currentZone = allElements.find((el) => el._id === currentZoneId);
      if (currentZone) {
        const currentZoneData = this.transformElementToZoneData(currentZone);
        if (currentZoneData) {
          zones.push(currentZoneData);
        }
      }
    }

    return zones;
  }
  /**
   * Flatten all elements from map levels into a single array
   */
  private flattenMapElements(mapLevels: any[]): any[] {
    return mapLevels.reduce((acc, mapLevel) => {
      if (mapLevel.elements) {
        acc.push(...mapLevel.elements);
      }
      return acc;
    }, []);
  }
  /**
   * Transform API element to standardized ZoneData format
   */
  private transformElementToZoneData(element: any): ZoneData | null {
    if (!element.geoFence?.geometry) return null;

    const geometry = element.geoFence.geometry;
    const baseZone: Partial<any> = {
      _id: element._id,
      name: element.name,
      level: element.level,
      nature: element.nature || 'UNKNOWN',
      parentId: element.parentId,
    };

    switch (geometry.type) {
      case 'Circle':
        return {
          ...baseZone,
          type: 'Circle',
          center: [geometry.center.lat, geometry.center.lng],
          radius: geometry.radius,
          centroid: [geometry.center.lat, geometry.center.lng],
        } as ZoneData;

      case 'Polygon':
        const coordinates = geometry.coordinates.map((coord: any) => [
          coord.lat,
          coord.lng,
        ]);
        const centroid = this.calculatePolygonCentroid(coordinates);

        return {
          ...baseZone,
          type: 'Polygon',
          coordinates: coordinates,
          centroid: centroid,
        } as ZoneData;

      default:
        console.warn(`Unsupported geometry type: ${geometry.type}`);
        return null;
    }
  }
  /**
   * Calculate polygon centroid
   */
  private calculatePolygonCentroid(
    coordinates: [number, number][]
  ): [number, number] {
    const latSum = coordinates.reduce((sum, [lat]) => sum + lat, 0);
    const lngSum = coordinates.reduce((sum, [, lng]) => sum + lng, 0);

    return [latSum / coordinates.length, lngSum / coordinates.length];
  }
}
